import React, { useContext, useState, useEffect, useCallback } from 'react';
import { FiUser, FiBell, FiFolder, FiMenu } from 'react-icons/fi';
import { useLocation } from 'react-router-dom';
import { AuthContext } from '../../context/AuthContext';
import { useProject } from '../../context/ProjectContext';
import { useMenu } from '../../context/MenuContext';
import NotificationPanel from '../Common/NotificationPanel';
import visitService from '../../services/visitService';
import actionItemService from '../../services/actionItemService';
import './Header.css';
import { CTMS_NOTIFICATIONS_REFRESH_EVENT } from '../../utils/constants';

const PI_PROFILE_CODE = 'PI';

/** Vector power icon (no raster); color from parent via `currentColor`. */
const LogoutPowerIcon = ({ className }) => (
  <svg
    className={className}
    viewBox="0 0 24 24"
    width="24"
    height="24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden
    focusable="false"
  >
    <path
      d="M18.36 6.64a9 9 0 1 1-12.73 0"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <line
      x1="12"
      y1="2"
      x2="12"
      y2="12"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const Header = ({ user, onLogout, onToggleSidebar, sidebarOpen }) => {
  const location = useLocation();
  const { user: authUser } = useContext(AuthContext);
  const { projectInfo: contextProjectInfo, currentProject } = useProject();
  const { getPageTitle } = useMenu();
  const [showNotifications, setShowNotifications] = useState(false);
  const [notificationCount, setNotificationCount] = useState(0);

  const profileCode = (
    authUser?.profileCode
    || authUser?.ProfileCode
    || authUser?.profile
    || authUser?.Profile
    || authUser?.vUserTypeCode
    || authUser?.role
    || ''
  ).toString().trim().toUpperCase();
  const isPI = profileCode === PI_PROFILE_CODE.toUpperCase();

  const studyProjectCode = (() => {
    const fromInfo = (contextProjectInfo?.projectCode || contextProjectInfo?.ProjectCode || '').toString().trim();
    if (fromInfo) return fromInfo;
    const cp = (currentProject || '').toString().trim();
    if (!cp || cp.toLowerCase() === 'all') return '';
    return cp;
  })();

  const fetchNotificationCount = useCallback(async () => {
    try {
      if (isPI) {
        const [visitPending, followUpPending] = await Promise.all([
          visitService.getPINotifications(studyProjectCode),
          visitService.getPIFollowUpNotifications(studyProjectCode),
        ]);
        const n1 = Array.isArray(visitPending) ? visitPending.length : 0;
        const n2 = Array.isArray(followUpPending) ? followUpPending.length : 0;
        setNotificationCount(n1 + n2);
      } else {
        const data = await actionItemService.getOverdueNotifications(studyProjectCode);
        setNotificationCount(Array.isArray(data) ? data.length : 0);
      }
    } catch {
      setNotificationCount(0);
    }
  }, [isPI, studyProjectCode]);

  useEffect(() => {
    if (authUser) fetchNotificationCount();
    else setNotificationCount(0);
  }, [authUser, fetchNotificationCount, location.pathname]);

  useEffect(() => {
    const onRefresh = () => {
      if (authUser) fetchNotificationCount();
    };
    window.addEventListener(CTMS_NOTIFICATIONS_REFRESH_EVENT, onRefresh);
    return () => window.removeEventListener(CTMS_NOTIFICATIONS_REFRESH_EVENT, onRefresh);
  }, [authUser, fetchNotificationCount]);

  useEffect(() => {
    const onProject = () => {
      if (authUser) fetchNotificationCount();
    };
    window.addEventListener('projectUpdated', onProject);
    return () => window.removeEventListener('projectUpdated', onProject);
  }, [authUser, fetchNotificationCount]);

  const handleNotificationCountChange = useCallback((count) => {
    setNotificationCount(typeof count === 'number' ? count : 0);
  }, []);

  const displayProjectInfo = contextProjectInfo;

  return (
    <header className="header">
      <div className="header-left">
        <button
          type="button"
          className="header-menu-btn"
          onClick={onToggleSidebar}
          title={sidebarOpen ? 'Close menu' : 'Open menu'}
          aria-label={sidebarOpen ? 'Close menu' : 'Open menu'}
        >
          <FiMenu size={24} />
        </button>
        <h1 className="page-title">{getPageTitle(location.pathname)}</h1>
      </div>

      <div className="header-right">
        {/* Project Details - only project code visible */}
        <div className="project-info" title={displayProjectInfo ? (displayProjectInfo.projectDescription || displayProjectInfo.projectCode) : 'Select a project from Visit Scheduling or Site pages'}>
          <FiFolder size={18} />
          <span className="project-details-label">Project:</span>
          {displayProjectInfo ? (
            <span className="project-code">{displayProjectInfo.projectCode}</span>
          ) : (
            <span className="project-none">No project selected</span>
          )}
        </div>

        <div className="user-info">
          <FiUser size={20} />
          <span className="user-name">
            {(() => {
              const firstName = (authUser?.firstName ?? authUser?.FirstName ?? '').toString().trim();
              const lastName = (authUser?.lastName ?? authUser?.LastName ?? '').toString().trim();
              const fullName = [firstName, lastName].filter(Boolean).join(' ');
              const username = (authUser?.username ?? authUser?.Username ?? authUser?.userId ?? '').toString().trim();
              const profile = (
                authUser?.profileDescription
                || authUser?.ProfileDescription
                || authUser?.profileCode
                || authUser?.ProfileCode
                || authUser?.profile
                || authUser?.Profile
                || authUser?.vUserTypeCode
                || authUser?.role
                || ''
              ).toString().trim();
              // "FirstName LastName (UserName)(ProfileDescription)" — space before login; no space between )( for profile
              if (fullName) {
                let t = fullName;
                if (username) t += ` (${username})`;
                if (profile) t += `(${profile})`;
                return t;
              }
              if (username && profile) return `${username}(${profile})`;
              if (username) return username;
              return profile || 'User';
            })()}
          </span>
        </div>

        <button
          className="notification-btn"
          onClick={() => setShowNotifications(prev => !prev)}
        >
          <FiBell size={20} />
          {notificationCount > 0 && (
            <span className="badge">{notificationCount > 99 ? '99+' : notificationCount}</span>
          )}
        </button>

        <NotificationPanel
          isOpen={showNotifications}
          onClose={() => setShowNotifications(false)}
          onNotificationCountChange={handleNotificationCountChange}
        />

        <button
          type="button"
          className="logout-btn"
          onClick={onLogout}
          title="Logout"
          aria-label="Logout"
        >
          <LogoutPowerIcon className="logout-btn-icon" />
        </button>
      </div>
    </header>
  );
};

export default Header;